import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import {
  countTeamsForUserAndEvent,
  createRecord,
  createRecords,
  fetchRecord,
  getUserByEmail,
  updateRecord,
} from "config/airtable";
import ContactInformation from "./sections/ContactInformation";
import TeamInformation from "./sections/TeamInformation";
import StudentInformation from "./sections/StudentInformation";
import KitRequest from "./sections/KitRequest";

const RegistrationPage = () => {
  const { eventID: paramEventID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [eventID, setEventID] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [teamCount, setTeamCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // State for user info fields
  const [userInfoFields, setUserInfoFields] = useState({
    id: null,
    first_name: { data: "", error: "" },
    last_name: { data: "", error: "" },
    email: { data: "", error: "" },
    phone_number: { data: "", error: "" },
  });

  const makeDefaults = (minStudents) => {
    const blankStudent = {
      first_name: "",
      middle_name: "",
      last_name: "",
      school: "",
      grade: "",
      emergency_full_name: "",
      emergency_email: "",
      emergency_phone_number: "",
    };
    return {
      team_name: "",
      kit_requested: false,
      students: Array.from({ length: minStudents }).map(() => ({
        ...blankStudent,
      })),
    };
  };

  // Update value or error for user info fields
  const updateUserInfoField = (field, key, value) => {
    setUserInfoFields((prevState) => ({
      ...prevState,
      [field]: { ...prevState[field], [key]: value },
    }));
  };

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      team_name: "",
      kit_requested: false,
      students: [
        {
          first_name: "",
          middle_name: "",
          last_name: "",
          school: "",
          grade: "",
          emergency_full_name: "",
          emergency_email: "",
          emergency_phone_number: "",
        },
      ],
    },
    mode: "onBlur",
  });

  const watchedStudents = useWatch({
    control,
    name: "students",
    defaultValue: [],
  });

  const filledCount = (
    Array.isArray(watchedStudents) ? watchedStudents : []
  ).filter(
    (s) => s.first_name?.trim() !== "" && s.last_name?.trim() !== ""
  ).length;

  const {
    fields: studentFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "students",
  });

  // Set hardcoded event ID or URL-based event ID
  useEffect(() => {
    if (
      location.pathname === "/rockets/register" ||
      location.pathname === "/rocket/register/" ||
      location.pathname === "/rtc/register"
    ) {
      setEventID("reciCY3fWqNor6eH4");
    } else {
      setEventID(paramEventID);
    }
  }, [location.pathname, paramEventID]);

  // Fetch event details from Airtable
  useEffect(() => {
    const loadEvent = async () => {
      if (!eventID) return;
      setLoading(true);
      try {
        const eventRecord = await fetchRecord("events", eventID);
        setEvent(eventRecord);
      } catch (error) {
        console.error("Error fetching event:", error);
        setErrorMessage(error.message);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [eventID]);

  // after your loadEvent effect, add this:
  useEffect(() => {
    if (!event) return;

    // 1) figure out how many blanks we need
    const min = event.data["min_students_per_team"] || 1;

    // 2) grab whatever the user has already typed in the other fields
    const current = getValues();

    // 3) overwrite only `students`, leave team_name/kit_requested alone
    reset({
      ...current,
      students: makeDefaults(min).students,
    });
  }, [event, getValues, reset]);

  // Use effect to fetch user record data if email is stored in local storage
  useEffect(() => {
    const email = localStorage.getItem("email");
    setUserInfoFields((prev) => ({
      ...prev,
      email: { data: email || "", error: "" },
    }));

    if (email) {
      const loadUser = async () => {
        setLoading(true);
        try {
          const userRecord = await getUserByEmail(email);
          if (userRecord) {
            setUserInfoFields((prev) => ({
              ...prev,
              id: userRecord.id,
              email: { data: userRecord.data.email, error: "" },
              first_name: { data: userRecord.data.first_name, error: "" },
              last_name: { data: userRecord.data.last_name, error: "" },
              phone_number: { data: userRecord.data.phone_number, error: "" },
            }));
            const count = await countTeamsForUserAndEvent(
              userRecord.id,
              eventID
            );
            setTeamCount(count);
          }
        } catch (error) {
          console.error("Error fetching user by email:", error);
          setUserInfoFields((prev) => ({
            ...prev,
            id: null,
            first_name: { data: "", error: "" },
            last_name: { data: "", error: "" },
            phone_number: { data: "", error: "" },
          }));
        } finally {
          setLoading(false);
        }
      };
      loadUser();
    }
  }, [eventID]);

  // Handle form submission
  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Step 1: Check to see if user exists in Airtable
      let userId = userInfoFields.id;
      if (!userId || userId === null) {
        const newUserRecordData = {
          first_name: userInfoFields.first_name.data,
          last_name: userInfoFields.last_name.data,
          email: userInfoFields.email.data,
          phone_number: userInfoFields.phone_number.data,
        };
        const newUserRecord = await createRecord("users", newUserRecordData);
        userId = newUserRecord.id;
        setUserInfoFields((prev) => ({
          ...prev,
          id: userId,
        }));
      } else {
        // Update existing user record with any changes
        await updateRecord("users", userId, {
          first_name: userInfoFields.first_name.data,
          last_name: userInfoFields.last_name.data,
          email: userInfoFields.email.data,
          phone_number: userInfoFields.phone_number.data,
        });
      }

      // Step 2: Register each student
      const studentRecords = data.students.map((student) => ({
        fields: {
          first_name: student.first_name,
          middle_name: student.middle_name,
          last_name: student.last_name,
          FK_school: [student.school.value],
          grade: parseInt(student.grade, 10),
          emergency_full_name: student.emergency_full_name,
          emergency_email: student.emergency_email,
          emergency_phone_number: student.emergency_phone_number,
        },
      }));

      console.log("Student records to create:", studentRecords);
      const createdStudents = await createRecords("students", studentRecords);
      console.log("Created students:", createdStudents);
      const studentIDs = createdStudents.map((student) => student.id);

      // Step 3: Create the team
      const teamRecord = {
        team_name: data.team_name,
        fk_team_members_id: studentIDs,
        fk_ext_registered_event_id: [eventID],
        fk_team_leader_id: [userId],
        kit_requested: data.kit_requested,
      };

      await createRecord("teams", teamRecord);
      reset(makeDefaults(event.data["min_students_per_team"] || 1));
      setTeamCount((prev) => prev + 1);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting registration:", error);
      toast.error(`Error submitting registration: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !eventID) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading event information...
        </Typography>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Event not found. Please check the URL or try again later.
        </Typography>
        <Typography variant="body1" color="error">
          URL: {window.location.href}
        </Typography>
        <Typography variant="body1" color="error">
          Error: {errorMessage}
        </Typography>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, textAlign: "center" }}>
        {/* <Typography variant="h4" gutterBottom>
          {`Your team has been successfully registered for the ${new Date(
            event.data["Event Start"]
          ).getFullYear()} ${event.data["Event Name"]}!`}
        </Typography> */}
        <Typography variant="h4" gutterBottom>
          {`Your team has been successfully registered for the ${new Date(
            event.data["Event Start"]
          ).getFullYear()} ${event.data["Event Name"]}!`}
        </Typography>
        <Typography variant="body1" gutterBottom>
          Please check your email for your team registration confirmation and
          event details. If you have any questions, please contact the event
          coordinator at eventcoordinator@most.org.
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            onClick={() => {
              setSubmitted(false);
              reset(makeDefaults(event.data["min_students_per_team"] || 1));
            }}
          >
            Register Another Team
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled
            sx={{ mr: 2 }}
            onClick={() => {
              navigate("/edit-team");
            }}
          >
            Edit Team
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Navigate to home page
              navigate("/");
            }}
          >
            Go to Home Page
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <ToastContainer position="bottom-center" autoClose={5000} />
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h4" gutterBottom>
          {`${new Date(event.data["Event Start"]).getFullYear()} ${
            event.data["Event Name"] || "Event"
          } Registration`}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <ContactInformation
            userInfoFields={userInfoFields}
            setUserInfoFields={setUserInfoFields}
            updateUserInfoField={updateUserInfoField}
            setTeamCount={setTeamCount}
            eventID={eventID}
          />
          <Divider sx={{ my: 2 }} />
          <TeamInformation register={register} errors={errors} />
          <Divider sx={{ my: 2 }} />
          <StudentInformation
            control={control}
            errors={errors}
            fields={studentFields}
            append={append}
            remove={remove}
            minStudents={event?.data["min_students_per_team"] || 1}
            maxStudents={event?.data["max_students_per_team"] || 8}
          />
          <Divider sx={{ my: 2 }} />
          <KitRequest
            control={control}
            teamCount={teamCount}
            maxKitsPerUser={event?.data["max_team_per_user"] || 0}
          />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ textAlign: "center", my: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={
                submitting ||
                filledCount < (event?.data["min_students_per_team"] || 1)
              }
            >
              {submitting ? "Submitting Registration" : "Submit Registration"}
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
};

export default RegistrationPage;
